import React from 'react';
import { Form, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import GoBackButton from '../GoBackButton';

import {
  Button,
  Checkbox,
  CheckboxGroup,
  Input,
  Link,
  Stack,
  Text,
} from '@/components';
import { useInputAttributes, useTranslation } from '@/hooks';
import { routes } from '@/statics';
import { persianCharactersRegex, yupValidator } from '@/utils';

type CustomerPrimaryFormData = {
  first_name: string;
  last_name: string;
};

type CustomerPrimaryFormProps = {
  goBack: () => void;
  data: Partial<CustomerPrimaryFormData>;
  onSubmit: (data: CustomerPrimaryFormData) => void;
  submitLoading: boolean;
};

const CustomerPrimaryForm: React.FC<CustomerPrimaryFormProps> = ({
  goBack,
  data,
  onSubmit,
  submitLoading,
}) => {
  const [allChecked, setAllChecked] = React.useState<boolean>(false);

  const { t } = useTranslation('pages.signUp.form.customerPrimaryForm');

  const validationSchema = yupValidator.object().shape({
    first_name: yupValidator
      .string()
      .max(50)
      .label(t('first_name'))
      .required()
      .matches(persianCharactersRegex, t('firstNamePersian')),
    last_name: yupValidator
      .string()
      .max(50)
      .label(t('last_name'))
      .required()
      .matches(persianCharactersRegex, t('lastNamePersian')),
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: data as CustomerPrimaryFormData,
  });

  const filedInputProps = useInputAttributes<CustomerPrimaryFormData>(
    control,
    register,
    errors,
    { first_name: t('first_name'), last_name: t('last_name') }
  );

  return (
    <Stack flex={1} w="100%" h="100%" align="flex-start" gap={4}>
      <Form
        control={control}
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <Stack
          w="100%"
          flexDir={{ base: 'column', lg: 'row' }}
          align="flex-start"
          justify="space-between"
          gap={{ base: 4, lg: 10 }}
        >
          <Input {...filedInputProps.first_name} />
          <Input {...filedInputProps.last_name} />
        </Stack>
      </Form>
      <Stack w="full" align="center" gap={6} justify="end" py={4} flexGrow={1}>
        <Stack w="100%" gap={6}>
          <CheckboxGroup
            onChange={(list) => {
              if (list.length === 1) {
                setAllChecked(true);
              }
              if (list.length !== 1) {
                setAllChecked(false);
              }
            }}
          >
            <Checkbox value="rulesRule">
              <Stack flexDir="row">
                <Link
                  href={routes.staticPages.href('activity_regulations')}
                  color="secondary.600"
                  target="_blank"
                  textDecoration="underline"
                  textUnderlineOffset={6}
                  _hover={{
                    color: 'secondary.700',
                    textDecoration: 'underline',
                  }}
                >
                  {t('rulesPlan')}
                </Link>
                <Text>{t('iRead')}</Text>
              </Stack>
            </Checkbox>
          </CheckboxGroup>
        </Stack>
        <Button
          w="full"
          type="submit"
          isDisabled={!allChecked || submitLoading}
          onClick={(payload) => void handleSubmit(onSubmit)(payload)}
        >
          {t('next')}
        </Button>
        <GoBackButton goBack={goBack} />
      </Stack>
    </Stack>
  );
};

export default CustomerPrimaryForm;
